/**
 * The workspace's people on the Access screen (ADR-034), for workspaces of
 * any size: a list of people is shown a page at a time with a name filter,
 * and a roster that failed to load says so — an empty list would read as a
 * workspace with nobody in it.
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { VaultHolderMemberDto } from '@/lib/api';
import { memberLabel } from './shared';

/** People shown at once in a list of the workspace's members. */
export const MEMBER_PAGE_SIZE = 50;

/** Whether a member matches a name filter (display name or username, any case). */
function matchesFilter(member: VaultHolderMemberDto, filter: string): boolean {
  const needle = filter.trim().toLowerCase();
  if (!needle) return true;
  return [member.displayName, member.username, memberLabel(member, member.principalId)].some(
    (text) => !!text && text.toLowerCase().includes(needle),
  );
}

export interface MemberPage {
  /** The people on the page shown. */
  rows: readonly VaultHolderMemberDto[];
  /** Everyone the filter matches. */
  matching: number;
  page: number;
  pageCount: number;
  filter: string;
  setFilter: (filter: string) => void;
  setPage: (page: number) => void;
  /** Whether the list is long enough to need the filter and the pager. */
  paged: boolean;
}

/** One page of `members`, filtered by name. A new filter goes back to the first page. */
export function useMemberPage(members: readonly VaultHolderMemberDto[]): MemberPage {
  const [filter, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const matching = useMemo(
    () => members.filter((m) => matchesFilter(m, filter)),
    [members, filter],
  );
  const pageCount = Math.max(1, Math.ceil(matching.length / MEMBER_PAGE_SIZE));
  // The roster can shrink under a page (a refetch); never show past its end.
  const shown = Math.min(page, pageCount - 1);
  return {
    rows: matching.slice(shown * MEMBER_PAGE_SIZE, (shown + 1) * MEMBER_PAGE_SIZE),
    matching: matching.length,
    page: shown,
    pageCount,
    filter,
    setFilter: (next) => {
      setFilterText(next);
      setPage(0);
    },
    setPage,
    paged: members.length > MEMBER_PAGE_SIZE,
  };
}

/**
 * The name filter above a paged list of people. `label` names the list when a
 * screen shows more than one.
 */
export function MemberFilter({
  page,
  id,
  label = 'Find a person',
}: {
  page: MemberPage;
  id: string;
  label?: string;
}) {
  if (!page.paged) return null;
  return (
    <Input
      id={id}
      type="search"
      aria-label={label}
      placeholder="Find a person…"
      className="h-8 max-w-xs"
      value={page.filter}
      onChange={(e) => page.setFilter(e.target.value)}
    />
  );
}

/** "21–40 of 230" and Previous / Next, below a paged list of people. */
export function MemberPager({
  page,
  label = 'Pages of people',
}: {
  page: MemberPage;
  /** Names the pager when a screen shows more than one list. */
  label?: string;
}) {
  if (!page.paged) return null;
  if (page.matching === 0) {
    return <p className="text-xs text-muted-foreground">Nobody matches “{page.filter}”.</p>;
  }
  const first = page.page * MEMBER_PAGE_SIZE + 1;
  const last = Math.min(page.matching, (page.page + 1) * MEMBER_PAGE_SIZE);
  return (
    <nav
      aria-label={label}
      className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"
    >
      <span>
        {first}–{last} of {page.matching === 1 ? '1 person' : `${page.matching} people`}
      </span>
      {page.pageCount > 1 && (
        <span className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page.page === 0}
            onClick={() => page.setPage(page.page - 1)}
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page.page >= page.pageCount - 1}
            onClick={() => page.setPage(page.page + 1)}
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </span>
      )}
    </nav>
  );
}

/**
 * The workspace's people did not load. Said as such, with a retry — never
 * shown as an empty workspace, which would offer to set roles up for nobody.
 */
export function MembersLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="space-y-2 text-sm" role="alert">
      <p className="text-destructive">
        The workspace’s people could not be loaded, so who holds which role cannot be shown.
      </p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
